import { DeleteFilled, TeamOutlined } from '@ant-design/icons';
import { createEnumOptions } from '@/utils/core/data-transformation';
import { getEnumNamespace } from '@/utils/infrastructure';
import { CoreEnums } from '@/utils/domain/constants';
import Filters, { registerModuleFilters } from '@/utils/input/filters';

const PlatformFilter = {
  ...Filters.BaseFilters.EnumFilter,
  name: 'reportPlatform',
  label: 'reports.platform',
  enumNamespace: getEnumNamespace(CoreEnums.ReportPlatform),
  enumSource: CoreEnums.ReportPlatform,
  options: createEnumOptions(CoreEnums.ReportPlatform, 'reports.platform'),
};

const TypeFilter = {
  ...Filters.BaseFilters.EnumFilter,
  name: 'type',
  label: 'global.type',
  enumNamespace: getEnumNamespace(CoreEnums.ReportType),
  enumSource: CoreEnums.ReportType,
  options: createEnumOptions(CoreEnums.ReportType, 'reports.type').filter(
    (opt) => opt.value !== CoreEnums.ReportType.Administration,
  ),
};

const GroupFilter = {
  ...Filters.BaseFilters.RemoteFilter,
  name: 'reportGroupId',
  label: 'global.text.group',
  // User-facing filter on `/reports` → uses the access-aware
  // `report/groups` endpoint so non-admin roles only see the groups
  // they're allowed to filter by (matches the standard adopted by
  // `entity/groups`, `list/groups`, `process/groups`). The admin
  // CRUD endpoint `reportGroup/paged` is reserved for
  // `/configuration/reports`.
  apiRoute: 'report/groups',
  params: {
    select: 'id,name',
    filter: 'isActive eq true',
    orderBy: 'name asc',
  },
};

const ProcessFilter = {
  ...Filters.BaseFilters.RemoteFilter,
  name: 'processId',
  label: 'global.text.process',
  // `/reports` is user-facing (no admin gate on the route). Use the
  // launch-permission endpoint so non-admin roles only see the processes
  // they can actually filter reports by, matching the base ProcessFilter
  // default (`@/utils/input/filters/ProcessFilter`).
  apiRoute: 'process/allowed',
  params: {
    select: 'id,name',
    filter: 'isActive eq true',
    orderBy: 'name asc',
  },
};

const IsPublicFilter = {
  ...Filters.BaseFilters.BooleanFilter,
  name: 'isPublic',
  label: 'global.text.isPublic',
};

const CreatedDateFilter = {
  ...Filters.DateFilter,
  label: 'global.text.createdDate',
};

const UpdatedDateFilter = {
  ...Filters.DateFilter,
  label: 'global.text.updatedDate',
  name: 'updatedDateRange',
};

export const filtersConfig = [
  PlatformFilter,
  TypeFilter,
  GroupFilter,
  ProcessFilter,
  IsPublicFilter,
  Filters.IsActiveFilter,
  CreatedDateFilter,
  UpdatedDateFilter,
];

export const adminReportsFiltersConfig = filtersConfig.filter((f) => f.name !== 'type');

registerModuleFilters('reports', [
  PlatformFilter,
  TypeFilter,
  GroupFilter,
  ProcessFilter,
  IsPublicFilter,
]);

registerModuleFilters('reportAdminReports', adminReportsFiltersConfig);

export const getReportOptionsItems = (formatMessage) => [
  {
    key: 'collaborators',
    label: formatMessage({ id: 'collaboratorsDrawer.title' }),
    icon: <TeamOutlined />,
  },
  {
    key: 'delete',
    label: formatMessage({ id: 'global.text.delete' }),
    icon: <DeleteFilled />,
    danger: true,
  },
];

/**
 * Per-item menu options for the reports grid kebab.
 *
 * Mirrors the lists/entities/apps/agents pattern: every command is
 * gated by the per-item `canManage` flag from `/report/manage` (admins
 * + collaborators receive `true`, regular users always get `false`).
 * Hide the kebab entirely for read-only consumers so the tile doesn't
 * advertise commands the backend would reject.
 */
export const getReportOptionsItemsForItem = (item, formatMessage) => {
  if (item?.canManage !== true) return [];
  return getReportOptionsItems(formatMessage);
};
